import { ScrollStrategy } from "@angular/cdk/overlay";
import { ViewContainerRef } from "@angular/core";

export type AutoFocusTarget = "dialog" | "first-tabbable" | "first-heading";

export type DrawerPosition = "left" | "right";

export class VdsDrawerConfig<D = any> {
    /**
     * Where the attached component should live in Angular's *logical* component tree.
     * This affects what is available for injection and the change detection order for
     * the component instantiated inside the drawer. This does not affect where the drawer content will actually be rendered.
     */
    viewContainerRef?: ViewContainerRef;

    /**
     * ID for the drawer. If omitted, a unique one will be generated.
     */
    id?: string;

    /**
     * Custom class for the backdrop
     */
    backdropClass?: string | string[] = "vds-drawer-backdrop";

    /**
     * Whether the user can use escape or clicking outside to close drawer
     */
    disableClose?: boolean = false;

    /**
     * Width of the drawer
     */
    width?: string = "";

    /**
     * Height of the drawer
     */
    height?: string = "100%";

    /**
     * Position of the drawer
     */
    position: DrawerPosition = "right";

    /**
     * Data being injected into the child component.
     */
    data?: D | null = null;

    /** ID of the element that describes the drawer */
    ariaDescribedBy?: string | null = null;

    /** ID of the element that labels the drawer */
    ariaLabelledBy?: string | null = null;

    /** Aria label to assign to the drawer element */
    ariaLabel?: string | null = null;

    /** Whether this is a modal drawer. Used to set the `aria-modal` attribute. */
    ariaModal?: boolean = true;

    /** Whether the drawer should focus the first focusable element on open */
    autoFocus?: AutoFocusTarget | string | boolean = "first-tabbable";

    /**
     * Whether the drawer should restore focus to the
     * previously-focused element, after it's closed.
     */
    restoreFocus?: boolean = true;

    /** Whether to wait for the opening animation to finish before trapping focus. */
    delayFocusTrap?: boolean = true;

    /** Scroll strategy to be used for the drawer */
    scrollStrategy?: ScrollStrategy;

    /** Disable vertical scroll */
    disableScroll?: boolean = false;

    /** Whether the drawer should close when the user goes backwards/forwards in history */
    closeOnNavigation?: boolean = true;
}
